/* ========================================================================= */
/* Background Color */
/* ========================================================================= */

import { useCallback, useEffect, useState } from 'react';

/**
 * @function getBgColor
 * @param {string} backgroundColor
 * @return {string}
 *
 * Return corresponding background classname
 */
export const getBgColor = (backgroundColor) => {
    let bg = '';

    switch (backgroundColor) {
        case 'Blue':
            bg = '#C2E8EA';
            break;
        case 'Green':
            bg = '#DFE2AF';
            break;
        case 'Peach':
            bg = '#F7E6DB';
            break;
        case 'Yellow':
            bg = '#F3F2A9';
            break;
        case 'Orange':
            bg = '#F3F2A9';
            break;
        case 'Teal':
            bg = '#F3F2A9';
            break;
        case 'Asphalt':
            bg = '#000000';
            break;
        case 'Brand Orange':
            bg = '#FC5200';
            break;
        case 'Sand':
            bg = '#F3F2ED';
            break;
    }

    return bg;
};

/**
 * @function getColorStyles
 * @param {string} backgroundColor
 * @return {string}
 *
 * Return corresponding background and border colours
 */
export const getColorStyles = (backgroundColor) => {
    let style = {
        backgroundColor: '#FFF', // white
        borderColor: '#ECECEC' // border-grey
    };

    switch (backgroundColor) {
        case 'Blue':
            style = {
                backgroundColor: '#C2E8EA', // pastel-blue
                borderColor: '#B6E4EC' // border-blue
            };
            break;
        case 'Green':
            style = {
                backgroundColor: '#DFE2AF', // pastel-green
                borderColor: '#DDE1A5' // border-green
            };
            break;
        case 'Peach':
            style = {
                backgroundColor: '#F7E6DB', // pastel-peach
                borderColor: '#FADFD1' // border-peach
            };
            break;
        case 'Yellow':
            style = {
                backgroundColor: '#F3F2A9', // pastel-yellow
                borderColor: '#F0EFA5' // border-yellow
            };
            break;
        case 'Orange':
            style = {
                backgroundColor: '#FC5200', // orange
            };
            break;
        case 'Teal':
            style = {
                backgroundColor: '#78DAE5', // teal
            };
            break;
        case 'Instagram':
            style = {
                backgroundColor: '#8a3ab9'
            };
            break;
        case 'Twitter':
            style = {
                backgroundColor: '#1DA1F2'
            };
            break;
        case 'X':
            style = {
                backgroundColor: '#101419'
            };
            break;
        case 'Facebook':
            style = {
                backgroundColor: '#4267B2'
            };
            break;
    }

    return style;
};

/* ========================================================================= */
/* Methods(s) */
/* ========================================================================= */

const MODULE_HEADING_FIELDS = {
    BvaModuleTextAndImage: 'title',
    BvaModuleTextCards: 'header',
    BvaModuleAccordion: 'heading',
    BvaModuleWalkthrough: 'heading',
    BvaModuleFeature: 'heading',
    BvaModuleBento: 'title',
    BvaModuleText: 'heading',
    BvaModuleStats: 'header',
};

const slugify = (/** @type {string} */ str) => (str || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-');

/**
 * @function getModuleSlug
 * @param {*} module
 * @returns {string | null}
 * Returns the anchor slug for a navigable module, or null if it isn't one.
 */
export const getModuleSlug = (module) => {
    if (!module || typeof module !== 'object') { return null; }
    const headingField = MODULE_HEADING_FIELDS[/** @type {keyof typeof MODULE_HEADING_FIELDS} */ (module.__typename)];
    if (!headingField) { return null; }
    return slugify(module[headingField] || '');
};

/**
 * @function findModuleAnchors
 * @param {*} data
 * @returns {Array<{ label: string, slug: string }>}
 * Finds all navigable modules and extracts their heading for use in AnchorNavigation
 */
export const findModuleAnchors = (data) => {
    /** @type {Array<{ label: string, slug: string }>} */
    let anchors = [];

    if (Array.isArray(data)) {
        data.forEach((d) => {
            anchors = anchors.concat(findModuleAnchors(d));
        });
    } else if (data && typeof data === 'object' && data.__typename) {
        const headingField = MODULE_HEADING_FIELDS[/** @type {keyof typeof MODULE_HEADING_FIELDS} */ (data.__typename)];
        if (headingField) {
            if (data.articleIncludeInAnchorNavigation) {
                const label = data[headingField] || '';
                anchors.push({ label, slug: slugify(label) });
            }
        } else {
            Object.keys(data).forEach((k) => { anchors = anchors.concat(findModuleAnchors(data[k])); });
        }
    }

    return anchors;
};

/**
 * @function getAllModules
 * @param {*} data
 * @returns {array}
 * Concatenates all module arrays
 */
export const getAllModules = (page) => {
    if (!page) {return null;}

    let modules = [];
    Object.keys(page).map(key => {
        if(key.includes('modules')) {
            modules = [...modules, ...page[key].items];
        }
    });

    return modules;
};



export const usePersistedState = (defaulValue, key, { sync } = { sync: true }) => {
    const [value, setValue] = useState(defaulValue);
    const [expireAt, setExpireAt] = useState(Infinity);
    const [isReady, setIsReady] = useState(false);

    const persistValue = useCallback((v, exp = Infinity) => {
        setValue(v);
        try {
            localStorage.setItem(key, JSON.stringify(v));
            localStorage.setItem(`${key}_exp`, JSON.stringify(exp));
            setExpireAt(exp);
        } catch { }
    }, [setValue, setExpireAt]);

    const clearValue = () => {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}_exp`);
        setValue(null);
        setValue(setExpireAt);
    };


    useEffect(() => {
        try {
            const expireAt = JSON.parse(localStorage.getItem(`${key}_exp`)) || Infinity;

            if (expireAt > new Date().valueOf()) {
                const persistedValue = JSON.parse(localStorage.getItem(key));
                setValue(persistedValue);
                setExpireAt(expireAt);
            }
        }
        catch { }

        setIsReady(true);
    }, []);


    // Keep storage in sync
    useEffect(() => {
        if (sync) {
            /** @param {StorageEvent} event */
            const handleStorageUpdate = (event) => {
                if (event.key === key) {
                    const expireAt = JSON.parse(localStorage.getItem(`${key}_exp`)) || Infinity;

                    if (expireAt > new Date().valueOf()) {
                        const persistedValue = JSON.parse(localStorage.getItem(key));
                        setValue(persistedValue);
                        setExpireAt(expireAt);
                    }
                }
            };

            window.addEventListener('storage', handleStorageUpdate);
            return () => {
                window.removeEventListener('storage', handleStorageUpdate);
            };
        }
    }, [sync]);


    return {
        value,
        setValue: persistValue,
        clearValue,
        isReady,
        expireAt
    };
};
